import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent
} from 'react';

import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import VolumeOffIcon from '@smart-tv/components/icons/volume-off';
import VolumeDownIcon from '@smart-tv/components/icons/volume-down';
import VolumeUpIcon from '@smart-tv/components/icons/volume-up';
import {
  retrievePersistentData,
  storePersistentData
} from '@smart-tv/utils/persistent-data/persistent-data';

import { usePlayerContext } from '../../_contexts/player-context';
import usePlayerEvent from '../../_hooks/use-player-event';
import { PlayerEvent } from '../../types';
import { PLAYER_FOCUS_KEY_SOUND } from '../../player-helpers';
import styles from './player-controls-volume-button.module.scss';
import { testIds } from '@smart-tv/e2e/shared/variables';

const DEFAULT_VOLUME = 0.5;
const FALLBACK_VOLUME = 0.1;

const PlayerControlsVolumeControl = () => {
  const { player } = usePlayerContext();

  const [muted, setMuted] = useState<boolean>(
    () => retrievePersistentData<boolean>('player-is-muted') ?? false
  );
  const [volume, setVolume] = useState<number>(() => {
    const stored = retrievePersistentData<number>('player-volume');
    return stored && stored > 0 ? stored : DEFAULT_VOLUME;
  });

  const sliderTrackRef = useRef<HTMLDivElement | null>(null);

  // State is the single source of truth: these effects persist it and push it
  // to the player. Handlers below only call setState — no manual syncing.
  useEffect(() => {
    storePersistentData('player-volume', volume, false);
    player?.setVolume(volume);
  }, [player, volume]);

  useEffect(() => {
    storePersistentData('player-is-muted', muted, false);
    muted ? player?.mute() : player?.unmute();
  }, [player, muted]);

  // The VO player can reset volume when a new stream opens, so re-assert it.
  const initializeVolume = useCallback(() => {
    player?.setVolume(volume);
    if (muted) player?.mute();
  }, [player, volume, muted]);

  usePlayerEvent(PlayerEvent.Opened, initializeVolume);

  const applyVolumeChange = useCallback((newVolumePercent: number) => {
    if (newVolumePercent <= 0) {
      setMuted(true);
      setVolume((prev) => (prev > 0 ? prev : FALLBACK_VOLUME));
    } else {
      setMuted(false);
      setVolume(newVolumePercent / 100);
    }
  }, []);

  const getSliderValue = useCallback((clientX: number): number => {
    if (!sliderTrackRef.current) return 0;
    const { left, width } = sliderTrackRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(100, Math.round(((clientX - left) / width) * 100)));
  }, []);

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.button === 2) return;

      applyVolumeChange(getSliderValue(event.clientX));

      const onMove = (e: MouseEvent) => applyVolumeChange(getSliderValue(e.clientX));
      const onUp = (e: MouseEvent) => {
        applyVolumeChange(getSliderValue(e.clientX));
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [applyVolumeChange, getSliderValue]
  );

  const handleMute = useCallback(() => setMuted(true), []);
  const handleUnmute = useCallback(() => setMuted(false), []);

  const displayVolume = muted ? 0 : volume * 100;
  const isMutedOrZero = muted || volume === 0;
  const isLowVolume = !muted && volume > 0 && volume <= 0.5;

  const toggleSoundTestId = testIds.player.playerControls.toggleSound;
  return (
    <div className={styles['volume-control']}>
      {isMutedOrZero ? (
        <IconButton
          size="xl"
          focusKey={PLAYER_FOCUS_KEY_SOUND}
          onClick={handleUnmute}
          icon={<VolumeOffIcon size="lg" />}
          data-testid={toggleSoundTestId}
          aria-label="Slå lyd til"
        >
          Lyd til
        </IconButton>
      ) : isLowVolume ? (
        <IconButton
          size="xl"
          focusKey={PLAYER_FOCUS_KEY_SOUND}
          onClick={handleMute}
          icon={<VolumeDownIcon size="lg" />}
          data-testid={toggleSoundTestId}
          aria-label="Slå lyd fra"
        >
          Lyd fra
        </IconButton>
      ) : (
        <IconButton
          size="xl"
          focusKey={PLAYER_FOCUS_KEY_SOUND}
          onClick={handleMute}
          icon={<VolumeUpIcon size="lg" />}
          data-testid={toggleSoundTestId}
          aria-label="Slå lyd fra"
        >
          Lyd fra
        </IconButton>
      )}

      <div
        className={styles['volume-slider']}
        onMouseDown={handleMouseDown}
        tabIndex={0}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={displayVolume}
        data-testid={testIds.player.playerControls.volumeSlider}
      >
        <div ref={sliderTrackRef} className={styles['volume-slider__track']} />
        <div className={styles['volume-slider__range']} style={{ width: `${displayVolume}%` }} />
        <div
          className={styles['volume-slider__handle']}
          style={{ left: `calc(${displayVolume}% - 7px)` }}
        />
      </div>
    </div>
  );
};

export default memo(PlayerControlsVolumeControl);
